/**
 * ProfileSelector Component
 * Displays user profiles in a flexible grid with checkboxes for selection
 */

import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { profileApi, Profile } from "../services/api";

interface ProfileSelectorProps {
    limit?: number;
    onSelectionChange?: (selectedProfiles: Profile[]) => void;
    className?: string;
    style?: React.CSSProperties;
}

export const ProfileSelector: React.FC<ProfileSelectorProps> = ({
    limit = 1,
    onSelectionChange,
    className,
    style,
}) => {
    const { dbUser } = useAuth();
    const [profiles, setProfiles] = useState<Profile[]>([]);
    const [selectedProfiles, setSelectedProfiles] = useState<Profile[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Load profiles when component mounts
    useEffect(() => {
        if (dbUser?.id) {
            loadProfiles();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dbUser?.id]);

    const loadProfiles = async () => {
        if (!dbUser?.id) return;

        try {
            setLoading(true);
            setError(null);
            const response = await profileApi.getUserProfiles(dbUser.id, {
                page: 1,
                page_size: 100, // Most users won't have more than 100 profiles
            });
            setProfiles(response.items);

            // Auto-select the main profile (first one) if available
            if (response.items.length > 0) {
                const mainProfile = response.items[0];
                setSelectedProfiles([mainProfile]);
                // Immediately notify parent of auto-selection
                onSelectionChange?.([mainProfile]);
            }
        } catch (err) {
            console.error("Error loading profiles:", err);
            setError("Failed to load profiles");
        } finally {
            setLoading(false);
        }
    };

    const handleProfileToggle = useCallback(
        (profile: Profile) => {
            setSelectedProfiles((current) => {
                const isSelected = current.some((p) => p.id === profile.id);
                let newSelection: Profile[];

                if (isSelected) {
                    // Deselect the profile
                    newSelection = current.filter((p) => p.id !== profile.id);
                } else {
                    // Select the profile
                    if (limit === 1) {
                        // If limit is 1, replace the current selection
                        newSelection = [profile];
                    } else {
                        // If limit > 1, add to selection (if under limit)
                        if (current.length < limit) {
                            newSelection = [...current, profile];
                        } else {
                            // Already at limit, don't add
                            return current;
                        }
                    }
                }

                onSelectionChange?.(newSelection);
                return newSelection;
            });
        },
        [limit, onSelectionChange]
    );

    const isProfileSelected = useCallback(
        (profile: Profile) => {
            return selectedProfiles.some((p) => p.id === profile.id);
        },
        [selectedProfiles]
    );

    const isProfileDisabled = useCallback(
        (profile: Profile) => {
            // If already selected, not disabled
            if (isProfileSelected(profile)) return false;
            // If under limit, not disabled
            if (selectedProfiles.length < limit) return false;
            // At limit and not selected, so disabled
            return true;
        },
        [selectedProfiles, limit, isProfileSelected]
    );

    const containerStyle: React.CSSProperties = {
        ...style,
    };

    const headerStyle: React.CSSProperties = {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "16px",
    };

    const selectionCountStyle: React.CSSProperties = {
        fontSize: "14px",
        color: "#6b7280",
        fontWeight: 500,
    };

    const gridStyle: React.CSSProperties = {
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
        gap: "12px",
        width: "100%",
    };

    const profileCardStyle: React.CSSProperties = {
        display: "flex",
        alignItems: "center",
        padding: "12px",
        borderRadius: "8px",
        border: "1px solid #e5e7eb",
        backgroundColor: "#ffffff",
        cursor: "pointer",
        transition: "all 0.2s",
        userSelect: "none",
    };

    const getProfileCardHoverStyle = (
        profile: Profile
    ): React.CSSProperties => ({
        ...profileCardStyle,
        borderColor: isProfileSelected(profile) ? "#3b82f6" : "#d1d5db",
        backgroundColor: isProfileSelected(profile) ? "#eff6ff" : "#f9fafb",
        opacity: isProfileDisabled(profile) ? 0.5 : 1,
        cursor: isProfileDisabled(profile) ? "not-allowed" : "pointer",
    });

    const checkboxStyle: React.CSSProperties = {
        width: "18px",
        height: "18px",
        marginRight: "12px",
        accentColor: "#3b82f6",
        cursor: "pointer",
    };

    const profileInfoStyle: React.CSSProperties = {
        flex: 1,
    };

    const profileNameStyle: React.CSSProperties = {
        fontWeight: 600,
        fontSize: "14px",
        color: "#1f2937",
        marginBottom: "2px",
    };

    const profileMetaStyle: React.CSSProperties = {
        fontSize: "12px",
        color: "#6b7280",
    };

    const mainBadgeStyle: React.CSSProperties = {
        marginLeft: "6px",
        fontSize: "10px",
        backgroundColor: "#3b82f6",
        color: "white",
        padding: "2px 6px",
        borderRadius: "3px",
        fontWeight: 500,
    };

    const loadingStyle: React.CSSProperties = {
        textAlign: "center",
        padding: "32px",
        color: "#6b7280",
    };

    const errorStyle: React.CSSProperties = {
        textAlign: "center",
        padding: "32px",
        color: "#dc2626",
        backgroundColor: "#fef2f2",
        borderRadius: "8px",
        border: "1px solid #fecaca",
    };

    if (loading) {
        return (
            <div className={className} style={containerStyle}>
                <div style={loadingStyle}>Loading profiles...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={className} style={containerStyle}>
                <div style={errorStyle}>
                    <div style={{ marginBottom: "8px" }}>⚠️</div>
                    <div>{error}</div>
                </div>
            </div>
        );
    }

    if (profiles.length === 0) {
        return (
            <div className={className} style={containerStyle}>
                <div style={loadingStyle}>
                    <div style={{ marginBottom: "8px" }}>👤</div>
                    <div>No profiles found</div>
                </div>
            </div>
        );
    }

    return (
        <div className={className} style={containerStyle}>
            <div style={headerStyle}>
                <div style={selectionCountStyle}>
                    {selectedProfiles.length} of {limit} selected
                </div>
            </div>

            <div style={gridStyle}>
                {profiles.map((profile, index) => (
                    <div
                        key={profile.id}
                        style={profileCardStyle}
                        onClick={() => {
                            if (!isProfileDisabled(profile)) {
                                handleProfileToggle(profile);
                            }
                        }}
                        onMouseEnter={(e) => {
                            Object.assign(
                                e.currentTarget.style,
                                getProfileCardHoverStyle(profile)
                            );
                        }}
                        onMouseLeave={(e) => {
                            Object.assign(
                                e.currentTarget.style,
                                profileCardStyle
                            );
                            if (isProfileSelected(profile)) {
                                e.currentTarget.style.borderColor = "#3b82f6";
                                e.currentTarget.style.backgroundColor =
                                    "#eff6ff";
                            }
                            if (isProfileDisabled(profile)) {
                                e.currentTarget.style.opacity = "0.5";
                                e.currentTarget.style.cursor = "not-allowed";
                            }
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={isProfileSelected(profile)}
                            disabled={isProfileDisabled(profile)}
                            onChange={() => {}} // Handled by card click
                            style={checkboxStyle}
                        />
                        <div style={profileInfoStyle}>
                            <div style={profileNameStyle}>
                                {profile.name}
                                {index === 0 && (
                                    <span style={mainBadgeStyle}>Main</span>
                                )}
                            </div>
                            <div style={profileMetaStyle}>
                                Created{" "}
                                {new Date(
                                    profile.created_at
                                ).toLocaleDateString()}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
